import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ILearnerList } from 'src/app/interfaces/ilearner-list';
import { ReportingService } from 'src/app/services/reporting/reporting.service';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslocoService } from '@jsverse/transloco';
import { LocaleService } from 'src/app/services/locale.service';
import { AppConstants } from 'src/app/app-constants';
@Component({
  selector: 'app-learner-list-modal',
  templateUrl: './learner-list-modal.component.html',
  styleUrls: ['./learner-list-modal.component.scss'],
  standalone: false
})
export class LearnerListModalComponent {
  sortField: string = "lastName";
  sortAscending: boolean = true;
  sortIcon: string = "/assets/icons/arrow/arrow-narrow-up.svg";
  count: number = 0;
  percentage: number = 0;
  label: string = '';
  labelIcon: string = '';
  isLearnerListLoading: boolean = true;
  category: string = '';
  learnerList: ILearnerList[] = [];
  isReportDownloading: boolean = false;
  isErrorReportDownloading: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<LearnerListModalComponent>,
    private reportingService: ReportingService,
    private gaService: GoogleAnalyticsService,
    private amplitudeService: AmplitudeService,
    private translocoService: TranslocoService,
    private localeService: LocaleService,
    private logger: LoggerService,
  ) { }

  ngOnInit(): void {
    this.count = this.data.count;
    this.percentage = this.data.percentage;
    this.category = this.data.category;
    this.getLabel();
    this.getLearnerList(this.data.accountUuid, this.data.moduleAssignmentUuid, this.data.category, this.data.moduleAssignmentType);
  }

  close(): void {
    this.dialogRef.close();
  }

  setSortField(sortField: string) {
    this.sortField = sortField;
    this.toggleSortOrder();
  }

  toggleSortOrder() {
    // this.sortField = sortField;
    this.sortAscending = !this.sortAscending;
    if (this.sortAscending) {
      this.sortIcon = '/assets/icons/arrow/arrow-narrow-down.svg';
    } else {
      this.sortIcon = '/assets/icons/arrow/arrow-narrow-up.svg';
    }
    this.sortList();
  }

  sortList() {
    if (!this.learnerList || !this.sortField) return;
    const sortAscending = this.sortAscending !== undefined ? this.sortAscending : true;
    this.learnerList.sort((a: any, b: any) => {
      let fieldA = a[this.sortField];
      let fieldB = b[this.sortField];
      if (this.sortField === 'lastLogin') {
        // Treat as date
        fieldA = new Date(fieldA);
        fieldB = new Date(fieldB);
        return sortAscending ? fieldA - fieldB : fieldB - fieldA;
      } else if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        // Treat as string
        return sortAscending ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
      } else if (typeof fieldA === 'number' && typeof fieldB === 'number') {
        // Treat as number
        return sortAscending ? fieldA - fieldB : fieldB - fieldA;
      }
      return sortAscending ? ('' + fieldA).localeCompare('' + fieldB) : ('' + fieldB).localeCompare('' + fieldA);
    });
  }

  formatLastLoginDates() {
    this.learnerList.forEach((item: any) => {
      if (item.lastLogin === null) {
        item.formatedDate = `${this.translocoService.translate('NEVER_LOGGED_IN', {}, 'reporting')}`;
      } else {
        const dateObj = new Date(item.lastLogin);
        const dateStr = this.localeService.formatDate(dateObj);
        const timeStr = this.localeService.formatTime(dateObj);
        item.formatedDate = `${dateStr} • ${timeStr}`;
      }
    });
  }

  getLearnerList(accountUuid: string, moduleAssignmentUuid: string, category: string, moduleAssignmentType: string) {
    this.reportingService.getLearnersList(accountUuid, moduleAssignmentUuid, category, moduleAssignmentType).subscribe({
      next: (res: ILearnerList[]) => {
        console.log(res);
        this.isLearnerListLoading = false;
        this.learnerList = res;
        this.formatLastLoginDates();
        this.setSortField(this.sortField);
      },
      error: (error) => {
        console.error('Error fetching module assignment report', error);
        this.logger.error('Error fetching module assignment report', error, { moduleAssignmentUuid: this.data.moduleAssignmentUuid });
      }
    });
  }

  getLabel() {
    const { category, moduleAssignmentType } = this.data;
    const labelMap = {
      NOT_STARTED: {
        label: this.translocoService.translate('ASSIGNMENT_NOT_STARTED', {}, 'reporting'),
        icon: '/assets/icons/yellow-circle.svg'
      },
      IN_PROGRESS: {
        label: (() => {
          if (moduleAssignmentType === AppConstants.TYPE_PRACTICE) return this.translocoService.translate('PRACTICED', {}, 'reporting');
          else if (moduleAssignmentType === AppConstants.TYPE_CERTIFICATION) return this.translocoService.translate('ASSIGNMENT_IN_PROGRESS', {}, 'reporting');
          else return this.translocoService.translate('PRACTICED', {}, 'reporting');
        })(),
        icon: '/assets/icons/blue-circle.svg'
      },
      COMPLETED: {
        label: (() => {
          if (moduleAssignmentType === AppConstants.TYPE_PRACTICE) return this.translocoService.translate('SCORE_CRITERIA_MET', {}, 'reporting');
          else if (moduleAssignmentType === AppConstants.TYPE_CERTIFICATION) return this.translocoService.translate('SCORE_CRITERIA_MET', {}, 'reporting');
          else return this.translocoService.translate('SCORE_CRITERIA_MET', {}, 'reporting');
        })(),
        icon: '/assets/icons/green-circle.svg'
      }
    };
    const { label, icon } = labelMap[category as keyof typeof labelMap] || { label: 'Unknown', icon: '' };
    this.label = label;
    this.labelIcon = icon;
  }

  downloadReport() {
    // Sending the Event of Clicking on 'Download CSV' to Amplitude
    this.amplitudeService.trackEvent('rollupreport_click_downloadcsv', {
      'learner_list_status': this.data.category,
    });

    if (!this.isReportDownloading) {
      this.isErrorReportDownloading = false;
      this.isReportDownloading = true;
      this.gaService.eventEmitter('Download learner activity report');
      // Account UUID
      const selectedAccountInfo = localStorage.getItem('selected_account_info');
      const currentAccountUuid = localStorage.getItem('current_account_uuid');
      const accountUuid = selectedAccountInfo ? JSON.parse(selectedAccountInfo).uuid : currentAccountUuid;
      //
      this.reportingService.getLearnersActivity(accountUuid, this.data.moduleAssignmentUuid, this.data.category, this.data.moduleAssignmentType).subscribe({
        next: (res: any) => {
          this.isReportDownloading = false;
          const blob = new Blob([res], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          const now = new Date();
          const timestamp = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}-${now.getMinutes().toString().padStart(2, '0')}-${now.getSeconds().toString().padStart(2, '0')}`;
          link.download = `${timestamp}_${this.data.moduleAssignmentName}_${this.data.category}.csv`;

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          this.isReportDownloading = false;
          this.isErrorReportDownloading = true;
          console.error('Error fetching module assignment report', error);
          this.logger.error('Error downloading learner activity report and fetching module assignment report', error, { moduleAssignmentUuid: this.data.moduleAssignmentUuid });
        }
      });
    }
  }
}
